import { Injectable } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';

/**
 * Proveedor de claves de idempotencia para mutaciones HTTP.
 *
 * Garantiza que cada mutación (POST/PATCH/PUT/DELETE) tenga una clave única
 * que permite al backend detectar y evitar duplicados en caso de reintentos.
 *
 * @example
 * ```typescript
 * const key = idempotencyProvider.generate();
 * const req = idempotencyProvider.attach(httpRequest, key);
 * // Usar en endpoints de mutación (POST/PATCH/PUT/DELETE)
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class IdempotencyKeyProvider {
  /**
   * Genera una nueva clave de idempotencia (UUID v4).
   *
   * @returns UUID v4 como string
   */
  generate(): string {
    return uuidv4();
  }

  /**
   * Adjunta la clave de idempotencia al header de la solicitud.
   *
   * @param request HttpRequest original
   * @param idempotencyKey Clave a adjuntar (si no se proporciona, genera una nueva)
   * @returns Nueva HttpRequest con el header Idempotency-Key
   */
  attach(request: HttpRequest<any>, idempotencyKey?: string): HttpRequest<any> {
    const key = idempotencyKey || this.generate();

    return request.clone({
      setHeaders: {
        'Idempotency-Key': key,
      },
    });
  }

  /**
   * Adjunta la clave de idempotencia solo si la solicitud es una mutación (POST/PATCH/PUT/DELETE).
   *
   * Útil para interceptors que deben aplicar idempotencia selectivamente.
   *
   * @param request HttpRequest original
   * @param idempotencyKey Clave a adjuntar (si no se proporciona, genera una nueva)
   * @returns HttpRequest modificado si es mutación, original si es GET/HEAD
   */
}
